// MasterDataContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useCurrency } from "./CurrencyContext";
import {
  getAllDestinationsApi,
  searchHotelAvailabilityApi,
  getAllPropertyTypesApi,
  getAllStarRatingsApi,
  getAllAmenityMasterApi,
  getAllRoomAmenityMasterApi,
} from "../../services/apiService";

const MasterDataContext = createContext();

export function useMasterData() {
  return useContext(MasterDataContext);
}

const SEARCH_STORAGE_KEY = "sh_search_state_v1";

const getStoredSearchParams = () => {
  try {
    const raw = sessionStorage.getItem(SEARCH_STORAGE_KEY) || localStorage.getItem(SEARCH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveStoredSearchParams = (params) => {
  try {
    const json = JSON.stringify(params);
    sessionStorage.setItem(SEARCH_STORAGE_KEY, json);
    localStorage.setItem(SEARCH_STORAGE_KEY, json);
  } catch {}
};

export function MasterDataProvider({ children }) {
  const [urlParams, setUrlParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedCurrency } = useCurrency();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const defaultParams = {
    destination: "Gurugram",
    destinationId: 101,
    hotelId: null,
    hotelName: "",
    checkInDate: formatDate(today),
    checkOutDate: formatDate(tomorrow),
    adults: 1,
    children: 0,
    childAges: [],
    noOfRooms: 1,
    targetCurrency: "INR",
  };

  const stored = getStoredSearchParams() || {};

  const parseChildAges = (val) => {
    if (!val) return [];
    if (typeof val === "string") {
      return val.split(",").map((s) => (s.trim() === "" ? "" : Number(s.trim()))).filter((n) => !isNaN(n));
    }
    if (Array.isArray(val)) {
      return val.map((item) => (typeof item === "object" && item !== null ? item.age : item));
    }
    return [];
  };

  const childAgesFromUrl = urlParams.get("childAges");
  const parsedChildAges = childAgesFromUrl
    ? parseChildAges(childAgesFromUrl)
    : (stored.childAges ? parseChildAges(stored.childAges) : (stored.children ? parseChildAges(stored.children) : defaultParams.childAges));

  const parsedChildrenCount = urlParams.get("children")
    ? parseInt(urlParams.get("children"), 10)
    : (stored.children !== undefined
        ? (Array.isArray(stored.children) ? stored.children.length : parseInt(stored.children, 10))
        : (parsedChildAges.length || defaultParams.children));

  const searchParams = {
    destination: urlParams.get("destination") || stored.destination || defaultParams.destination,
    destinationId: urlParams.get("destinationId")
      ? parseInt(urlParams.get("destinationId"), 10)
      : (stored.destinationId ? parseInt(stored.destinationId, 10) : defaultParams.destinationId),
    hotelId: urlParams.get("hotelId") || stored.hotelId || defaultParams.hotelId,
    hotelName: urlParams.get("hotelName") || stored.hotelName || (stored.hotelId ? stored.destination : "") || defaultParams.hotelName,
    checkInDate: urlParams.get("checkInDate") || stored.checkInDate || stored.checkIn || defaultParams.checkInDate,
    checkOutDate: urlParams.get("checkOutDate") || stored.checkOutDate || stored.checkOut || defaultParams.checkOutDate,
    adults: urlParams.get("adults")
      ? parseInt(urlParams.get("adults"), 10)
      : (stored.adults ? parseInt(stored.adults, 10) : defaultParams.adults),
    children: isNaN(parsedChildrenCount) ? 0 : parsedChildrenCount,
    childAges: parsedChildAges,
    noOfRooms: urlParams.get("noOfRooms")
      ? parseInt(urlParams.get("noOfRooms"), 10)
      : (stored.rooms || stored.noOfRooms ? parseInt(stored.rooms || stored.noOfRooms, 10) : defaultParams.noOfRooms),
    targetCurrency: urlParams.get("targetCurrency") || stored.targetCurrency || defaultParams.targetCurrency,
  };

  const [destinations, setDestinations] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [starRatings, setStarRatings] = useState([]);
  const [amenityMasters, setAmenityMasters] = useState([]);
  const [roomAmenityMasters, setRoomAmenityMasters] = useState([]);

  useEffect(() => {
    const fetchMasters = async () => {
      // Destinations
      try {
        const destRes = await getAllDestinationsApi();
        const candidateArray =
          destRes?.ResponsePacket?.DestinationList ||
          destRes?.ResponsePacket?.DestinationsList ||
          destRes?.ResponsePacket?.Destinations ||
          destRes?.ResponsePacket?.DestinationDetailList ||
          destRes?.ResponsePacket?.DestinationDetailsList ||
          (Array.isArray(destRes?.ResponsePacket) ? destRes.ResponsePacket : null) ||
          destRes?.data ||
          destRes?.Data ||
          (Array.isArray(destRes) ? destRes : null);

        if (Array.isArray(candidateArray) && candidateArray.length > 0) {
          setDestinations(candidateArray);
        } else {
          setDestinations([]);
        }
      } catch (err) {
        console.error("Failed to fetch destinations:", err);
      }

      // Property Types
      try {
        const ptRes = await getAllPropertyTypesApi();
        console.log("[MASTER] 🏠 Property Types RAW RESPONSE:", ptRes);
        console.log("[MASTER] 🏠 Property Types ResponsePacket:", ptRes?.ResponsePacket);
        const ptList =
          ptRes?.ResponsePacket?.PropertyTypeList ||
          ptRes?.ResponsePacket?.PropertyTypes ||
          ptRes?.ResponsePacket?.PropertyDetailList ||
          ptRes?.ResponsePacket?.PropertyDetails ||
          (Array.isArray(ptRes?.ResponsePacket) ? ptRes.ResponsePacket : null) ||
          (Array.isArray(ptRes) ? ptRes : []);
        console.log("[MASTER] 🏠 Property Types MAPPED LIST:", ptList);
        if (ptList[0]) console.log("[MASTER] 🏠 Property Type FIRST ITEM KEYS:", Object.keys(ptList[0]));
        setPropertyTypes(Array.isArray(ptList) ? ptList : []);
      } catch (err) {
        console.error("Failed to fetch property types:", err);
      }

      // Star Ratings
      try {
        const srRes = await getAllStarRatingsApi();
        console.log("[MASTER] ⭐ Star Ratings RAW RESPONSE:", srRes);
        console.log("[MASTER] ⭐ Star Ratings ResponsePacket:", srRes?.ResponsePacket);
        const srList =
          srRes?.ResponsePacket?.StarRatingList ||
          srRes?.ResponsePacket?.StarRatings ||
          srRes?.ResponsePacket?.StarRatingDetailList ||
          (Array.isArray(srRes?.ResponsePacket) ? srRes.ResponsePacket : null) ||
          (Array.isArray(srRes) ? srRes : []);
        console.log("[MASTER] ⭐ Star Ratings MAPPED LIST:", srList);
        if (srList[0]) console.log("[MASTER] ⭐ Star Rating FIRST ITEM KEYS:", Object.keys(srList[0]));
        setStarRatings(Array.isArray(srList) ? srList : []);
      } catch (err) {
        console.error("Failed to fetch star ratings:", err);
      }

      // Hotel Amenities (Master)
      try {
        const amRes = await getAllAmenityMasterApi();
        console.log("[MASTER] ⚙ Hotel Amenity Master RAW RESPONSE:", amRes);
        console.log("[MASTER] ⚙ Hotel Amenity Master ResponsePacket:", amRes?.ResponsePacket);
        const amList =
          amRes?.ResponsePacket?.AmenityList ||
          amRes?.ResponsePacket?.AmenityMasterList ||
          amRes?.ResponsePacket?.Amenities ||
          amRes?.ResponsePacket?.AmenityDetailList ||
          (Array.isArray(amRes?.ResponsePacket) ? amRes.ResponsePacket : null) ||
          (Array.isArray(amRes) ? amRes : []);
        console.log("[MASTER] ⚙ Hotel Amenity MAPPED LIST:", amList);
        if (amList[0]) console.log("[MASTER] ⚙ Hotel Amenity FIRST ITEM KEYS:", Object.keys(amList[0]));
        setAmenityMasters(Array.isArray(amList) ? amList : []);
      } catch (err) {
        console.error("Failed to fetch hotel amenities master:", err);
      }

      // Room Amenities (Master)
      try {
        const raRes = await getAllRoomAmenityMasterApi();
        console.log("[MASTER] 🛏 Room Amenity Master RAW RESPONSE:", raRes);
        console.log("[MASTER] 🛏 Room Amenity Master ResponsePacket:", raRes?.ResponsePacket);
        const raList =
          raRes?.ResponsePacket?.RoomAmenityList ||
          raRes?.ResponsePacket?.RoomAmenityMasterList ||
          raRes?.ResponsePacket?.RoomAmenities ||
          raRes?.ResponsePacket?.RoomAmenityDetailList ||
          (Array.isArray(raRes?.ResponsePacket) ? raRes.ResponsePacket : null) ||
          (Array.isArray(raRes) ? raRes : []);
        console.log("[MASTER] 🛏 Room Amenity MAPPED LIST:", raList);
        if (raList[0]) console.log("[MASTER] 🛏 Room Amenity FIRST ITEM KEYS:", Object.keys(raList[0]));
        setRoomAmenityMasters(Array.isArray(raList) ? raList : []);
      } catch (err) {
        console.error("Failed to fetch room amenities master:", err);
      }
    };
    fetchMasters();
  }, []);

  /* ── NEW: live "draft" state ─────────────────────────────────
     This is what the header pills AND the search widget read from
     and write to on every click/change — BEFORE the user hits the
     actual Search button. Because it lives in this shared context,
     any component that calls useMasterData() sees the same values
     instantly, no matter which one made the change. */
  const [draft, setDraft] = useState(() => ({
    destination: searchParams.destination,
    destinationId: searchParams.destinationId,
    hotelId: searchParams.hotelId,
    hotelName: searchParams.hotelName,
    checkInDate: searchParams.checkInDate,
    checkOutDate: searchParams.checkOutDate,
    adults: searchParams.adults,
    childAges: searchParams.childAges,
    noOfRooms: searchParams.noOfRooms,
  }));

  const updateDraft = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      try {
        const merged = { ...(getStoredSearchParams() || {}), ...next };
        saveStoredSearchParams(merged);
      } catch {}
      return next;
    });
  };

  const updateSearchParams = (newParams) => {
    const nextParams = { ...searchParams, ...newParams };
    saveStoredSearchParams(nextParams);

    // Keep draft in lockstep with whatever was just applied/searched,
    // so header + widget don't fall out of sync after a Search click.
    setDraft((prev) => ({ ...prev, ...newParams }));

    const urlSafe = {};
    for (const key in nextParams) {
      if (nextParams[key] !== null && nextParams[key] !== undefined && nextParams[key] !== "") {
        if (Array.isArray(nextParams[key])) {
          if (nextParams[key].length > 0) urlSafe[key] = nextParams[key].join(",");
        } else {
          urlSafe[key] = nextParams[key].toString();
        }
      }
    }

    setUrlParams(urlSafe);
    return new URLSearchParams(urlSafe).toString();
  };

  const searchAvailability = async (overrideParams = null) => {
    const payload = overrideParams ? { ...searchParams, ...overrideParams } : searchParams;
    const isDest = Boolean(payload.destination && !payload.hotelId);

    const checkIn = payload.checkInDate || searchParams.checkInDate || defaultParams.checkInDate;
    const checkOut = payload.checkOutDate || searchParams.checkOutDate || defaultParams.checkOutDate;
    const adultCount = Number(payload.adults || payload.numberOfAdults || 1) || 1;
    const childCount = Number(payload.children !== undefined ? payload.children : payload.numberOfChild) || 0;
    const roomCount = Number(payload.noOfRooms || payload.numberOfRooms || 1) || 1;
    const childAgesArray = Array.isArray(payload.childAges)
      ? payload.childAges.map((a) => (typeof a === "object" ? Number(a.age) : Number(a)))
      : [];

    const requestPayload = {
      searchType: isDest ? "destination" : "hotel",
      checkInDate: checkIn,
      checkOutDate: checkOut,
      adults: adultCount,
      numberOfAdults: adultCount,
      noOfAdults: adultCount,
      Adults: adultCount,
      children: childCount,
      numberOfChild: childCount,
      noOfChildren: childCount,
      childAges: childAgesArray,
      childrenAges: childAgesArray,
      noOfRooms: roomCount,
      numberOfRooms: roomCount,
      rooms: roomCount,
      currency: (overrideParams?.targetCurrency || overrideParams?.currency || selectedCurrency || (typeof window !== "undefined" ? localStorage.getItem("userCurrency") : "INR") || "INR").toUpperCase(),
      targetCurrency: (overrideParams?.targetCurrency || overrideParams?.currency || selectedCurrency || (typeof window !== "undefined" ? localStorage.getItem("userCurrency") : "INR") || "INR").toUpperCase(),
      guests: {
        adults: adultCount,
        children: childCount,
        noOfRooms: roomCount,
        childAges: childAgesArray,
      },
    };

    if (isDest) {
      requestPayload.destinationId = Number(payload.destinationId) || 101;
      requestPayload.destinationName = payload.destination || "Gurugram";
    } else {
      requestPayload.hotelId = Number(payload.hotelId);
      requestPayload.hotelName = payload.destination || payload.hotelName || "";
    }

    try {
      const res = await searchHotelAvailabilityApi(requestPayload);
      return res;
    } catch (err) {
      console.error("Search Availability failed:", err);
      throw err;
    }
  };

  return (
    <MasterDataContext.Provider
      value={{
        destinations,
        propertyTypes,
        starRatings,
        amenityMasters,
        roomAmenityMasters,
        searchParams,
        updateSearchParams,
        searchAvailability,
        draft,
        updateDraft,
      }}
    >
      {children}
    </MasterDataContext.Provider>
  );
}